import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { formatMoney } from '@/entrypoints/utils';
import { calendarConfigAtom, calendarLoadingAtom, selectedProductAtom } from './calendar-atoms';

type Props = {
  depositPrice: string;
  storePrice: string;
  reserveSlot: () => Promise<void>;
  onClose: () => void;
};

function CalendarConfirm({ depositPrice, storePrice, reserveSlot, onClose }: Props) {
  const {
    labels,
    klaviyoFormId,
    klaviyoFormIdMobile,
    products,
    payInStoreLabelPrice,
    payDepositLabelPrice,
  } = useAtomValue(calendarConfigAtom);
  const selectedProduct = useAtomValue(selectedProductAtom);
  const setLoading = useSetAtom(calendarLoadingAtom);

  const payInStoreEnabled = useMemo(
    () => products.find((p) => p.id.toString() === selectedProduct)?.pay_in_store,
    [products, selectedProduct],
  );

  const selectedProductData = useMemo(
    () => products.find((p) => p.id.toString() === selectedProduct),
    [products, selectedProduct],
  );

  // Older product configs omit `checkout_enabled`, so only an explicit false
  // hides the deposit checkout option.
  const depositCheckoutEnabled = selectedProductData?.checkout_enabled !== false;

  const handlePayInStore = useCallback(() => {
    const isMobile = window.innerWidth < 768;
    const productFormId = isMobile
      ? selectedProductData?.klaviyo_booking_form_id_mobile
      : selectedProductData?.klaviyo_booking_form_id_desktop;
    const defaultFormId = isMobile ? klaviyoFormIdMobile || klaviyoFormId : klaviyoFormId;
    const idToOpen = productFormId || defaultFormId;

    if (!idToOpen) return;

    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', idToOpen]);
    onClose();
  }, [selectedProductData, klaviyoFormId, klaviyoFormIdMobile, onClose]);

  const handlePayDepositNow = useCallback(() => {
    setLoading(true);
    reserveSlot().finally(() => setLoading(false));
  }, [reserveSlot, setLoading]);

  const payDepositDescription = useMemo(() => {
    return labels.pay_deposit_now_description
      .replace(
        /{deposit_amount}/g,
        formatMoney(selectedProductData?.price ?? 0, '${{amount_no_decimals}}'),
      )
      .replace(
        /{remaining_amount}/g,
        formatMoney(
          (selectedProductData?.compare_at_price ?? 0) - (selectedProductData?.price ?? 0),
          '${{amount_no_decimals}}',
        ),
      );
  }, [labels.pay_deposit_now_description, depositPrice, selectedProductData]);

  // CTA labels are rendered server-side; substitute them here from the same
  // per-product fields the captions use so the two never disagree.
  const payInStoreLabel = useMemo(() => {
    // Product metafield first, then per-block setting, then page-level fallback
    // from medispa-calendar-config.
    const amount =
      selectedProductData?.pay_in_store_amount ||
      selectedProductData?.pay_in_store_label_price ||
      payInStoreLabelPrice ||
      0;
    return labels.pay_in_store.replace(
      /{pay_in_store_amount}/g,
      formatMoney(amount, '${{amount_no_decimals}}'),
    );
  }, [labels.pay_in_store, selectedProductData, payInStoreLabelPrice]);

  const payDepositLabel = useMemo(() => {
    const amount = selectedProductData?.compare_at_price || payDepositLabelPrice || 0;
    return labels.pay_deposit_now.replace(
      /{total_amount}/g,
      formatMoney(amount, '${{amount_no_decimals}}'),
    );
  }, [labels.pay_deposit_now, selectedProductData, payDepositLabelPrice]);

  const payInStoreDescription = useMemo(() => {
    return labels.pay_in_store_description
      .replace(/{deposit_amount}/g, formatMoney(0, '${{amount_no_decimals}}'))
      .replace(
        /{remaining_amount}/g,
        formatMoney(selectedProductData?.pay_in_store_amount!, '${{amount_no_decimals}}'),
      );
  }, [labels.pay_in_store_description, selectedProductData]);

  return (
    <div className="medispa-calendar_confirm">
      {payInStoreEnabled && (
        <div className="medispa-calendar_confirm-actions">
          <button type="button" className="ba-btn outline" onClick={handlePayInStore}>
            {payInStoreLabel}
          </button>

          <div className="medispa-calendar-action--details-wrapper">
            <div className="medispa-calendar-action--details">
              {/* <strong className="price">{storePrice}</strong> */}
              <span className="description">{payInStoreDescription}</span>
            </div>
          </div>
        </div>
      )}

      {payInStoreEnabled && depositCheckoutEnabled && (
        <span className="medispa-calendar_confirm-or">{labels.or_choose_to}</span>
      )}

      {depositCheckoutEnabled && (
        <div className="medispa-calendar_confirm-actions accented">
          <button type="button" onClick={handlePayDepositNow} className="ba-btn dark solid">
            {payDepositLabel}
          </button>

          <div className="medispa-calendar-action--details-wrapper">
            <div className="medispa-calendar-action--details">
              {/* <strong className="price">{depositPrice}</strong> */}
              <span className="description">{payDepositDescription}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarConfirm;
